import { create } from 'zustand';
import { io } from 'socket.io-client';
import toast from 'react-hot-toast';
import { formatTime } from '../utils/formatDateTime';
import pushNotificationService from '../services/pushNotificationService';

let socket = null;

// Sound alert system
const alertAudioCtx = {
  ctx: null,
  getContext() {
    if (!this.ctx) {
      this.ctx = new (window.AudioContext || window.webkitAudioContext)();
    }
    return this.ctx;
  },
  play(severity) {
    try {
      const ctx = this.getContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);

      if (severity === 'CRITICAL') {
        // Urgent double-beep
        osc.frequency.value = 880;
        gain.gain.setValueAtTime(0.3, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);
        gain.gain.setValueAtTime(0.3, ctx.currentTime + 0.2);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.35);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.4);
      } else {
        // Single beep for HIGH
        osc.frequency.value = 660;
        gain.gain.setValueAtTime(0.2, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.25);
      }
    } catch (e) {
      // AudioContext not available or user hasn't interacted yet
    }
  },
};

function getSoundAlertPref() {
  return localStorage.getItem('kensafety-sound-alerts') !== 'off';
}

export const useSocketStore = create((set, get) => ({
  connected: false,
  activeAlerts: 0,
  latestAlert: null,
  compliance: null,
  subscribedSite: null,
  deviceHealth: {}, // { deviceId: { cpu, gpu, temp, timestamp } }
  zoneOccupancy: {}, // { zoneId: { count, timestamp, ... } }
  cameraStatuses: {}, // { cameraId: { status, timestamp } }
  soundAlerts: getSoundAlertPref(),

  toggleSoundAlerts: () => {
    const next = !get().soundAlerts;
    localStorage.setItem('kensafety-sound-alerts', next ? 'on' : 'off');
    set({ soundAlerts: next });
  },

  connect: () => {
    if (socket?.connected) return;

    // Clean up existing socket if any (prevents listener stacking on reconnect)
    if (socket) {
      socket.removeAllListeners();
      socket.disconnect();
      socket = null;
    }

    const token = localStorage.getItem('token');
    if (!token) return;

    // Note: Using default JSON parser for python-socketio compatibility
    socket = io('/dashboard', {
      auth: { token },
      transports: ['websocket'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });

    socket.on('connect', () => {
      set({ connected: true });
      console.log('Socket connected');

      // Re-subscribe to site if previously subscribed
      const siteId = get().subscribedSite;
      if (siteId) {
        socket.emit('subscribe:site', siteId);
      }
    });

    socket.on('disconnect', () => {
      set({ connected: false });
      console.log('Socket disconnected');
    });

    socket.on('connect_error', (err) => {
      console.error('Socket connect error:', err.message);
    });

    socket.io.on('reconnect', () => {
      console.log('Socket reconnected');
      const siteId = get().subscribedSite;
      if (siteId) {
        socket.emit('subscribe:site', siteId);
      }
    });

    socket.on('subscribed', ({ siteId, siteName }) => {
      console.log(`Subscribed to site: ${siteName}`);
    });

    // Handle new alerts
    socket.on('alert:new', (alert) => {
      set((state) => ({
        activeAlerts: state.activeAlerts + 1,
        latestAlert: alert
      }));

      // Play sound for critical/high alerts
      if (get().soundAlerts && (alert.severity === 'CRITICAL' || alert.severity === 'HIGH')) {
        alertAudioCtx.play(alert.severity);
      }

      // Show browser push notification (if enabled)
      try {
        pushNotificationService.showAlertNotification(alert);
      } catch (error) {
        console.warn('Failed to show push notification:', error);
      }

      // Show toast notification
      const severityColors = {
        CRITICAL: '🔴',
        HIGH: '🟠',
        MEDIUM: '🟡',
        LOW: '🔵',
      };

      toast.custom((t) => (
        <div className={`${t.visible ? 'animate-enter' : 'animate-leave'}
          max-w-lg w-full bg-white shadow-lg rounded-lg pointer-events-auto
          ring-1 ring-black ring-opacity-5`}
        >
          <div className="p-4">
            <div className="flex items-start">
              <div className="text-2xl mr-3 flex-shrink-0">
                {severityColors[alert.severity]}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">
                  {alert.type?.replace(/_/g, ' ') || 'Safety Alert'}
                </p>
                <p className="mt-1 text-sm text-gray-500">
                  {alert.subType?.replace(/_/g, ' ')}
                </p>
                <p className="mt-1 text-xs text-gray-400">
                  {formatTime(alert.detectedAt || alert.createdAt)}
                </p>
              </div>
              <button
                onClick={() => toast.dismiss(t.id)}
                className="ml-2 flex-shrink-0 text-gray-400 hover:text-gray-500"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {alert.snapshotUrl && (
              <div className="mt-3">
                <img
                  src={alert.snapshotUrl}
                  alt="Alert snapshot"
                  className="w-full h-32 object-cover rounded-md border border-gray-200"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            )}
          </div>
        </div>
      ), { duration: 15000 });
    });

    socket.on('alert:acknowledged', ({ alertId }) => {
      set((state) => ({
        activeAlerts: Math.max(0, state.activeAlerts - 1)
      }));
    });

    socket.on('alert:resolved', ({ alertId }) => {
      set((state) => ({
        activeAlerts: Math.max(0, state.activeAlerts - 1)
      }));
    });

    socket.on('compliance:update', (data) => {
      set({ compliance: data });
    });

    socket.on('device:online', ({ deviceName }) => {
      toast.success(`Device online: ${deviceName}`);
    });

    socket.on('device:offline', ({ deviceName }) => {
      toast.error(`Device offline: ${deviceName}`);
    });

    socket.on('device:health', (data) => {
      set((state) => ({
        deviceHealth: {
          ...state.deviceHealth,
          [data.deviceId]: data,
        },
      }));
    });

    // P0-3: Real-time zone occupancy updates
    socket.on('occupancy:update', (data) => {
      set((state) => ({
        zoneOccupancy: {
          ...state.zoneOccupancy,
          [data.siteId]: {
            ...data,
            receivedAt: new Date().toISOString(),
          },
        },
      }));
    });

    // P2-1: Real-time camera status updates
    socket.on('camera:status', (data) => {
      set((state) => ({
        cameraStatuses: {
          ...state.cameraStatuses,
          [data.cameraId]: {
            status: data.status,
            timestamp: data.timestamp,
          },
        },
      }));
    });

    socket.on('error', ({ message }) => {
      console.error('Socket error:', message);
    });
  },

  disconnect: () => {
    if (socket) {
      socket.removeAllListeners();
      socket.disconnect();
      socket = null;
    }
    set({
      connected: false,
      activeAlerts: 0,
      latestAlert: null,
      compliance: null,
      subscribedSite: null,
      deviceHealth: {},
      zoneOccupancy: {},
      cameraStatuses: {},
    });
  },

  subscribeSite: (siteId) => {
    if (socket?.connected && siteId) {
      // Unsubscribe from previous site
      const prevSite = get().subscribedSite;
      if (prevSite) {
        socket.emit('unsubscribe:site', prevSite);
      }

      socket.emit('subscribe:site', siteId);
      set({ subscribedSite: siteId });
    }
  },

  unsubscribeSite: (siteId) => {
    if (socket?.connected) {
      socket.emit('unsubscribe:site', siteId);
      set({ subscribedSite: null });
    }
  },

  acknowledgeAlert: (alertId) => {
    if (socket?.connected) {
      socket.emit('alert:acknowledge', { alertId });
    }
  },

  requestLatestData: (siteId) => {
    if (socket?.connected) {
      socket.emit('request:latest', { siteId });
    }
  },

  setActiveAlerts: (count) => {
    set({ activeAlerts: count });
  },
}));
